import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllRequirements, getAllDesigns, getAllFabricOptions, createOrder, createOrderLineItem } from '../../store/actions';

const inputCls = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400';
const newOrderNumber = () => `ORD-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(Date.now()).slice(-4)}`;
const EMPTY = { engagementId: '', requirementId: '', designId: '', fabricOptionId: '', product: '', targetDelivery: '' };

// Creates the order, then its colour/size line items (the backend assembles them into colourMatrix).
export default function CreateOrderForm({ engagements, initialStatus, onDone }) {
  const dispatch = useDispatch();
  const requirements = useSelector((state) => state.requirement.requirements);
  const designs = useSelector((state) => state.design.designs);
  const fabrics = useSelector((state) => state.fabricoption.options);
  const [form, setForm] = useState(EMPTY);
  const [lines, setLines] = useState([{ colour: '', size: '', quantity: '' }]);

  useEffect(() => {
    if (form.engagementId) dispatch(getAllRequirements({ engagementId: form.engagementId }));
  }, [dispatch, form.engagementId]);

  useEffect(() => {
    if (form.requirementId) {
      dispatch(getAllDesigns({ requirementId: form.requirementId }));
      dispatch(getAllFabricOptions({ requirementId: form.requirementId, decisionStatus: 4 }));
    }
  }, [dispatch, form.requirementId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value, ...(k === 'engagementId' ? { requirementId: '', designId: '', fabricOptionId: '' } : {}), ...(k === 'requirementId' ? { designId: '', fabricOptionId: '' } : {}) }));
  const setLine = (i, k) => (e) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: e.target.value } : l)));
  const totalQty = lines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);
  const num = (v) => (v === '' ? null : Number(v));

  const submit = (e) => {
    e.preventDefault();
    const filled = lines.filter((l) => l.colour.trim() && l.size.trim() && Number(l.quantity) > 0);
    dispatch(
      createOrder({
        payload: {
          engagementId: Number(form.engagementId),
          orderNumber: newOrderNumber(),
          requirementId: num(form.requirementId),
          designId: num(form.designId),
          fabricOptionId: num(form.fabricOptionId),
          product: form.product.trim(),
          totalQty: totalQty || null,
          status: initialStatus,
          targetDelivery: form.targetDelivery || null,
        },
        callback: {
          success: (order) => {
            filled.forEach((l) =>
              dispatch(createOrderLineItem({ payload: { orderId: order.id, colour: l.colour.trim(), size: l.size.trim(), quantity: Number(l.quantity) } })),
            );
          },
        },
      }),
    );
    setForm(EMPTY);
    setLines([{ colour: '', size: '', quantity: '' }]);
    onDone();
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Engagement *</label>
          <select required value={form.engagementId} onChange={set('engagementId')} className={inputCls}>
            <option value="">Select engagement</option>
            {engagements.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Product Name *</label>
          <input required value={form.product} onChange={set('product')} className={inputCls} />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Requirement</label>
          <select value={form.requirementId} onChange={set('requirementId')} disabled={!form.engagementId} className={inputCls}>
            <option value="">—</option>
            {form.engagementId && requirements.map((r) => <option key={r.id} value={r.id}>{[r.code, r.name].filter(Boolean).join(' · ')}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Target Delivery</label>
          <input type="date" value={form.targetDelivery} onChange={set('targetDelivery')} className={inputCls} />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Design</label>
          <select value={form.designId} onChange={set('designId')} disabled={!form.requirementId} className={inputCls}>
            <option value="">—</option>
            {form.requirementId && designs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Approved Fabric</label>
          <select value={form.fabricOptionId} onChange={set('fabricOptionId')} disabled={!form.requirementId} className={inputCls}>
            <option value="">—</option>
            {form.requirementId && fabrics.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[11px] font-medium text-gray-600">Colour / Size Quantities</label>
          <span className="text-[11px] text-gray-500">Total: <span className="font-mono font-semibold">{totalQty}</span> pcs</span>
        </div>
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
              <input placeholder="Colour" value={l.colour} onChange={setLine(i, 'colour')} className={inputCls} />
              <input placeholder="Size" value={l.size} onChange={setLine(i, 'size')} className={inputCls} />
              <input placeholder="Qty" type="number" min="0" value={l.quantity} onChange={setLine(i, 'quantity')} className={`${inputCls} font-mono`} />
              <button type="button" onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls))} className="px-2 text-gray-400 hover:text-red-500">×</button>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setLines((ls) => [...ls, { colour: '', size: '', quantity: '' }])} className="mt-2 text-[11px] text-blue-600 hover:underline">+ Add row</button>
      </div>

      <div className="flex gap-3 justify-end pt-2">
        <button type="button" onClick={onDone} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
        <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">Create Order</button>
      </div>
    </form>
  );
}
